// Fine couche au-dessus du SDK Telegram WebApp.
// Types minimaux (on ne déclare que ce qu'on utilise) + helpers sûrs hors Telegram.

export interface TgUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

export interface TgThemeParams {
  bg_color?: string;
  secondary_bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  header_bg_color?: string;
}

interface MainButton {
  text: string;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  setText(text: string): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
  setParams(p: Partial<{ text: string; color: string; text_color: string; is_active: boolean; is_visible: boolean }>): void;
}

interface BackButton {
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface HapticFeedback {
  impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: TgUser; auth_date?: number; query_id?: string };
  version: string;
  platform: string;
  colorScheme: "light" | "dark";
  themeParams: TgThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  MainButton: MainButton;
  BackButton: BackButton;
  HapticFeedback: HapticFeedback;
  ready(): void;
  expand(): void;
  close(): void;
  openInvoice(url: string, callback?: (status: "paid" | "cancelled" | "failed" | "pending") => void): void;
  openTelegramLink(url: string): void;
  /** Partage un message préparé (Bot API 8+) : ouvre le sélecteur natif. */
  shareMessage?(msgId: string, callback?: (sent: boolean) => void): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

/** L'instance WebApp si on tourne dans Telegram, sinon undefined (dev navigateur). */
export const tg: TelegramWebApp | undefined = window.Telegram?.WebApp;

/** Vrai si on est dans Telegram avec un initData signé exploitable. */
export function hasInitData(): boolean {
  return !!(tg && tg.initData && tg.initData.length > 0);
}

/** Initialise le SDK : signale prêt, passe en plein écran. Sans effet hors Telegram. */
export function initTelegram(): void {
  if (!tg) return;
  tg.ready();
  tg.expand();
}

/** Lien partagé : deep-link du bot (?startapp ouvre la Mini App en un tap). */
export const SHARE_URL = import.meta.env.VITE_SHARE_URL || "https://t.me/thebigdig_bot/thebigdig?startapp";

/** Ouvre la fenêtre de partage Telegram (un lien + un texte). */
export function shareLink(url: string, text: string): void {
  const link = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  if (tg?.openTelegramLink) tg.openTelegramLink(link);
  else window.open(link, "_blank");
}

/** Partage du score (lien standard vers le jeu). */
export function shareScore(text: string): void {
  shareLink(SHARE_URL, text);
}

/** Lien de parrainage personnel : le filleul arrive tracké (ref_<id>). */
export function referralUrl(userId: number): string {
  return userId > 0 ? `https://t.me/thebigdig_bot/thebigdig?startapp=ref_${userId}` : SHARE_URL;
}

/** Petit retour haptique défensif (no-op hors Telegram / API absente). */
export function haptic(kind: "light" | "medium" | "heavy" | "success" | "error" | "warning"): void {
  const h = tg?.HapticFeedback;
  if (!h) return;
  try {
    if (kind === "success" || kind === "error" || kind === "warning") {
      h.notificationOccurred(kind);
    } else {
      h.impactOccurred(kind);
    }
  } catch {
    /* certaines versions/clients ne supportent pas les haptics */
  }
}
